<div class="detail-page">
  <!-- Loading -->
  <div *ngIf="isLoading" class="detail-state">
    <mat-spinner [diameter]="40"></mat-spinner>
  </div>

  <!-- Not found / error -->
  <div *ngIf="!isLoading && (notFound || errorMessage)" class="detail-state">
    <lib-cdw-icon icon="error" size="40px"></lib-cdw-icon>
    <p class="detail-state-text">
      {{ notFound ? 'Customer not found.' : errorMessage }}
    </p>
    <lib-cdw-button level="primary" type="button" (buttonClick)="backToList()">
      Back to Customers
    </lib-cdw-button>
  </div>

  <!-- Loaded -->
  <ng-container *ngIf="!isLoading && customer as c">
    <div class="detail-header">
      <button type="button" class="back-link" (click)="backToList()">
        <lib-cdw-icon icon="arrow_back" size="18px"></lib-cdw-icon>
        <span>Back to Customers</span>
      </button>
      <h1 class="detail-name">{{ c.name }}</h1>
    </div>

    <!-- Universal details -->
    <section class="detail-card">
      <span class="section-title">Details</span>
      <lib-cdw-divider thickness="1px"></lib-cdw-divider>
      <div class="detail-grid">
        <div class="detail-item">
          <span class="section-subtitle">CDW Customer ID</span>
          <span class="section-content">{{ c.id }}</span>
        </div>
        <div class="detail-item">
          <span class="section-subtitle">Name</span>
          <span class="section-content">{{ c.name }}</span>
        </div>
        <div class="detail-item">
          <span class="section-subtitle">Primary Domain</span>
          <span class="section-content">{{ c.primaryDomain || '—' }}</span>
        </div>
        <div class="detail-item">
          <span class="section-subtitle">School District</span>
          <span class="section-content">{{ c.schoolDistrict || '—' }}</span>
        </div>
        <div class="detail-item">
          <span class="section-subtitle">Timezone</span>
          <span class="section-content">{{ c.timezone || '—' }}</span>
        </div>
        <div class="detail-item">
          <span class="section-subtitle">Date Format</span>
          <span class="section-content">{{ c.dateFormat || '—' }}</span>
        </div>
        <div class="detail-item">
          <span class="section-subtitle">Created</span>
          <span class="section-content">{{ c.createdAt ? (c.createdAt | date: 'medium') : '—' }}</span>
        </div>
        <div class="detail-item">
          <span class="section-subtitle">Onboarding</span>
          <span
            class="dwd-chip"
            [attr.data-tone]="onboardingTone(c.onboardingStatus)"
          >
            {{ onboardingLabel(c.onboardingStatus) }}
          </span>
          <!-- Always shown (incl. uninitialized): resetting a mid-onboarding
               tenant clears its DWD verification so QA can re-run Step 3. -->
          <lib-cdw-button
            level="secondary"
            type="button"
            [disabled]="resetting"
            (buttonClick)="confirmResetOnboarding()"
            data-testid="reset-onboarding"
          >
            {{ resetting ? 'Resetting…' : 'Reset onboarding' }}
          </lib-cdw-button>
        </div>
      </div>
      <p *ngIf="resetError" class="dwd-error">{{ resetError }}</p>
    </section>

    <!-- Onboarding progress (diagnostic; grows as wizard steps land) -->
    <section class="detail-card">
      <div class="dwd-header">
        <span class="section-title">Onboarding Progress</span>
        <!-- Re-polls just this panel so support can follow a tenant's live
             progress (e.g. a Step-4 sync flipping to complete) without a full
             page reload. -->
        <lib-cdw-button
          level="secondary"
          type="button"
          [disabled]="refreshingOnboarding"
          (buttonClick)="loadOnboarding(c.id)"
          data-testid="refresh-onboarding"
        >
          <lib-cdw-icon icon="refresh" size="18px"></lib-cdw-icon>
          {{ refreshingOnboarding ? 'Refreshing…' : 'Refresh' }}
        </lib-cdw-button>
      </div>
      <lib-cdw-divider thickness="1px"></lib-cdw-divider>

      <p *ngIf="onboardingError" class="dwd-error">
        Couldn't load onboarding progress.
        <button type="button" class="back-link" (click)="loadOnboarding(c.id)">
          Retry
        </button>
      </p>

      <ng-container *ngIf="onboarding as ob">
        <!-- Who holds the wizard's session lock right now (US-12). The lock is
             ephemeral, so an expired one simply reads as "no one". -->
        <div class="onboarding-active-session" data-testid="active-session">
          <span class="section-subtitle">Active Session</span>
          <div *ngIf="!ob.activeSession" class="section-content">
            No one is currently configuring this tenant.
          </div>
          <div *ngIf="ob.activeSession as session" class="onboarding-step">
            <span class="dwd-chip" data-tone="warn">Configuring</span>
            <span class="section-content">
              {{ session.name || session.email }}
            </span>
            <!-- "Started", not "since": the acquire time is preserved across
                 refreshes, so it does NOT track last activity — expiresAt is
                 what says whether the lock is being actively held. Absolute
                 time because this panel is a static fetch, so a countdown
                 would go stale the moment it rendered. -->
            <span class="section-subtitle">
              Started {{ session.since | date: 'medium' }}
              <ng-container *ngIf="session.expiresAt">
                · Expires at {{ session.expiresAt | date: 'medium' }}
              </ng-container>
              <ng-container *ngIf="session.name">— {{ session.email }}</ng-container>
            </span>
            <!-- Support lever: skip the 30-minute expiry when an admin's
                 browser died and a colleague needs in now. -->
            <lib-cdw-button
              level="secondary"
              type="button"
              [disabled]="releasingLock"
              (buttonClick)="releaseOnboardingLock(c.id)"
              data-testid="release-onboarding-lock"
            >
              {{ releasingLock ? 'Releasing…' : 'Release lock' }}
            </lib-cdw-button>
          </div>
          <p *ngIf="releaseLockError" class="dwd-error">{{ releaseLockError }}</p>
        </div>

        <!-- The Step-3 DWD propagation re-check (EDUS-2844).
             Unlike the sync run below, this is NOT cleared when it ends badly:
             the 24-hour timeout deliberately leaves the clock set, because that
             is what renders the tenant's terminal screen. So "Timed out" here is
             a live state support can act on, not leftover debris. -->
        <div
          *ngIf="ob.dwdRecheck as recheck"
          class="onboarding-active-session"
          data-testid="dwd-recheck"
        >
          <span class="section-subtitle">DWD Propagation Re-check</span>
          <div class="onboarding-step">
            <span
              class="dwd-chip"
              [attr.data-tone]="
                recheck.scopesVerified ? 'ok' : recheck.timedOut ? 'error' : 'warn'
              "
            >
              {{
                recheck.scopesVerified
                  ? 'Verified'
                  : recheck.timedOut
                    ? 'Timed out'
                    : 'Re-checking hourly'
              }}
            </span>
            <span class="section-subtitle">
              Started {{ recheck.startedAt | date: 'medium' }}
            </span>
            <!-- The action support actually needs: a timed-out tenant is not
                 stuck, they just have to press Verify again, which starts a
                 fresh 24 hours. Said here so nobody reaches for a full
                 reset-onboarding, which throws the whole wizard away. -->
            <span *ngIf="recheck.timedOut" class="section-subtitle">
              The 24 hours are up and re-checking has stopped. The admin can
              press Verify Scopes again to start a fresh attempt.
            </span>
            <!-- Only successful sends are removed, so anything still listed is
                 exactly who we could not reach. While re-checking is live the
                 next hourly run retries them; once it has timed out, nothing
                 will. -->
            <span class="section-subtitle" data-testid="dwd-pending-recipients">
              <ng-container
                *ngIf="recheck.pendingRecipients.length; else dwdAllNotified"
              >
                Awaiting notification:
                {{ recheck.pendingRecipients.join(', ') }}
                <ng-container *ngIf="recheck.timedOut"
                  >&nbsp;— re-checking has stopped, so these will not be retried
                  automatically.</ng-container
                >
              </ng-container>
              <ng-template #dwdAllNotified>
                No one is currently awaiting a notification.
              </ng-template>
            </span>
          </div>
        </div>

        <!-- The Step-4 sync run (EDUS-2770). Ephemeral Redis state, so absence
             means "nothing outstanding" rather than "never ran" — the durable
             answer is the Sync Data row in the checklist below. Shown because
             it is the first thing worth knowing when a tenant is sitting on the
             "Data sync has failed" retry screen. -->
        <div
          *ngIf="ob.initialSyncRun as run"
          class="onboarding-active-session"
          data-testid="initial-sync-run"
        >
          <span class="section-subtitle">Baseline Sync Run</span>
          <div class="onboarding-step">
            <!-- Three states, not two: a SUCCESS stamps no `lastOutcome` (only
                 failures do) and the run record outlives completion on its TTL,
                 so "no outcome" alone would show a finished run as Running for
                 days. `succeeded` is the completion timestamp, compared against
                 this run's start. -->
            <span
              class="dwd-chip"
              [attr.data-tone]="
                run.succeeded ? 'ok' : run.lastOutcome ? 'error' : 'warn'
              "
            >
              {{ run.succeeded ? 'Complete' : run.lastOutcome || 'Running' }}
            </span>
            <!-- A QA-simulated run has no job behind it, so there is no number
                 to show — rendering "Job #" with nothing after it implied one
                 existed and had gone missing. -->
            <span class="section-content">
              {{ run.jobId ? 'Job #' + run.jobId : 'QA-simulated run' }}
            </span>
            <span class="section-subtitle">
              Started {{ run.startedAt | date: 'medium' }}
            </span>
            <!-- A partial is treated as a failure at onboarding: the gate holds
                 and the admin is asked to run it again. Spelled out because the
                 word on its own reads like a success. -->
            <span *ngIf="run.lastOutcome === 'partial'" class="section-subtitle">
              Partial runs are treated as failures here — the tenant is held on
              Step 4 until a clean sync completes.
            </span>
            <!-- Drains as each email lands, so anything still listed after a
                 terminal outcome means delivery did not finish. -->
            <span class="section-subtitle" data-testid="sync-pending-recipients">
              <ng-container *ngIf="run.pendingRecipients.length; else allNotified">
                Awaiting notification: {{ run.pendingRecipients.join(', ') }}
              </ng-container>
              <ng-template #allNotified>
                <ng-container *ngIf="run.succeeded || run.lastOutcome"
                  >Everyone on the notification list has been emailed.</ng-container
                >
                <ng-container *ngIf="!run.succeeded && !run.lastOutcome"
                  >No one has asked to be notified yet.</ng-container
                >
              </ng-template>
            </span>
            <!-- Last-resort lever (EDUS-2858). Only offered once a run has
                 reached a terminal outcome, because until then retrying is the
                 answer. Releases the gate over data we KNOW is incomplete, and
                 it does not self-heal — the nightly sync is the same workflow
                 and fails the same way — so it is a deliberate human call, not
                 an automatic release after N failures. -->
            <lib-cdw-button
              *ngIf="run.lastOutcome"
              level="secondary"
              type="button"
              [disabled]="completingSync"
              (buttonClick)="completeInitialSync(c.id)"
              data-testid="complete-initial-sync"
            >
              {{ completingSync ? 'Releasing…' : 'Force sync complete' }}
            </lib-cdw-button>
          </div>
          <p *ngIf="completeSyncError" class="dwd-error">
            {{ completeSyncError }}
          </p>
        </div>

        <ul class="onboarding-steps" data-testid="onboarding-steps">
          <li *ngFor="let step of ob.steps" class="onboarding-step">
            <span
              class="dwd-chip"
              [attr.data-tone]="stepStatusTone(step.status)"
            >
              {{ stepStatusLabel(step.status) }}
            </span>
            <span class="section-content">{{ stepLabels[step.key] }}</span>
            <span class="section-subtitle">{{ step.detail }}</span>
          </li>
        </ul>

        <div class="onboarding-acceptances">
          <span class="section-subtitle">Legal Acceptances</span>
          <div
            *ngIf="ob.acceptances.length === 0"
            class="section-content"
          >
            No acceptances recorded yet.
          </div>
          <table
            *ngIf="ob.acceptances.length > 0"
            class="detail-table"
            data-testid="acceptance-table"
          >
            <thead>
              <tr>
                <th>User</th>
                <th>Document</th>
                <th>Version</th>
                <th>IP Address</th>
                <th>Accepted</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let a of ob.acceptances">
                <td>{{ a.userEmail }}</td>
                <td>{{ a.documentType }}</td>
                <td>{{ a.documentVersion }}</td>
                <td>{{ a.ipAddress || '—' }}</td>
                <td>{{ a.acceptedAt | date: 'medium' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </ng-container>
    </section>

    <!-- Asset Sync — current full-asset-sync (cache refresh) state: running +
         live counts, or the last run's status/time. Static fetch (a platform
         admin can't live-bind another tenant's data) + a Refresh button. -->
    <section class="detail-card">
      <div class="dwd-header">
        <span class="section-title">Asset Sync</span>
        <lib-cdw-button
          level="secondary"
          type="button"
          [disabled]="refreshingAssetSync"
          (buttonClick)="loadAssetSync(c.id)"
          data-testid="refresh-asset-sync"
        >
          <lib-cdw-icon icon="refresh" size="18px"></lib-cdw-icon>
          {{ refreshingAssetSync ? 'Refreshing…' : 'Refresh' }}
        </lib-cdw-button>
      </div>
      <lib-cdw-divider thickness="1px"></lib-cdw-divider>

      <p *ngIf="assetSyncError" class="dwd-error">
        Couldn't load asset sync status.
        <button type="button" class="back-link" (click)="loadAssetSync(c.id)">
          Retry
        </button>
      </p>

      <ng-container *ngIf="assetSync as s">
        <!-- Running: live progress counts from the active execution. -->
        <div
          *ngIf="s.running"
          class="onboarding-step"
          data-testid="asset-sync-running"
        >
          <span class="dwd-chip" [attr.data-tone]="'neutral'">Syncing…</span>
          <span class="section-content">
            Devices {{ s.counts?.devices ?? 0 }} · Users
            {{ s.counts?.users ?? 0 }} · Groupings {{ s.counts?.groupings ?? 0 }}
            written so far
          </span>
        </div>

        <!-- Idle: last completed/failed run. -->
        <div
          *ngIf="!s.running && s.lastStatus"
          class="onboarding-step"
          data-testid="asset-sync-last"
        >
          <span class="dwd-chip" [attr.data-tone]="assetSyncTone(s.lastStatus)">
            {{ assetSyncLabel(s.lastStatus) }}
          </span>
          <span class="section-content">
            Last sync {{ s.lastStatusAt | date: 'medium' }}
          </span>
        </div>

        <!-- Never synced. -->
        <div
          *ngIf="!s.running && !s.lastStatus"
          class="section-content"
          data-testid="asset-sync-none"
        >
          No sync has run for this customer yet.
        </div>
      </ng-container>
    </section>

    <!-- Users (provisioned/active) — who has access to this tenant. Server-side
         grid (paginate/filter/sort) over the `users` rows (signed in /
         provisioned); NOT pending invites, which aren't persisted. -->
    <section class="detail-card">
      <span class="section-title">Users</span>
      <lib-cdw-divider thickness="1px"></lib-cdw-divider>

      <p class="section-subtitle">
        Users who have signed in and been provisioned (not pending invites).
        "Reset Tour" clears a user's first-login stamp so their next sign-in
        lands on the tour page again.
      </p>

      <p *ngIf="resetTourError" class="dwd-error" data-testid="reset-tour-error">
        {{ resetTourError }}
      </p>

      <lib-cdw-ssrm-grid
        #usersGrid
        class="users-grid"
        viewKey="platform-customer-users"
        componentId="PlatformCustomerUsers"
        itemLabel="user"
        filterBySelectedColumnName="email"
        [columnDefs]="userColumnDefs"
        [filterBarSettings]="userFilterBarSettings"
        [cdwSearchGridDataService]="usersDataService"
        (cellClicked)="onUserCellClicked($event)"
        data-testid="users-grid"
      ></lib-cdw-ssrm-grid>
    </section>

    <!-- Domain-wide delegation -->
    <section class="detail-card">
      <div class="dwd-header">
        <span class="section-title">Domain-Wide Delegation</span>
        <div class="dwd-header-actions">
          <lib-cdw-button
            level="secondary"
            type="button"
            [disabled]="checking"
            (buttonClick)="checkDwd()"
          >
            <lib-cdw-icon icon="verified_user" size="18px"></lib-cdw-icon>
            {{ checking ? 'Checking…' : 'Check DWD' }}
          </lib-cdw-button>
          <!-- Per-scope breakdown: on demand only, and persists nothing, so it
               never disturbs the Status field below. -->
          <lib-cdw-button
            level="secondary"
            type="button"
            [disabled]="scopeChecking"
            (buttonClick)="checkScopes()"
            data-testid="check-dwd-scopes"
          >
            <lib-cdw-icon icon="checklist" size="18px"></lib-cdw-icon>
            {{ scopeChecking ? 'Checking…' : 'Check Scopes' }}
          </lib-cdw-button>
        </div>
      </div>
      <lib-cdw-divider thickness="1px"></lib-cdw-divider>

      <div class="detail-grid">
        <div class="detail-item">
          <span class="section-subtitle">Status</span>
          <span class="dwd-chip" [attr.data-tone]="statusTone(c.dwdCheckStatus)">
            {{ statusLabel(c.dwdCheckStatus) }}
          </span>
        </div>
        <div class="detail-item">
          <span class="section-subtitle">Last Checked</span>
          <span class="section-content">
            {{ c.lastDwdCheckAt ? (c.lastDwdCheckAt | date: 'medium') : '—' }}
          </span>
        </div>
        <div class="detail-item">
          <span class="section-subtitle">Delegate</span>
          <span class="section-content" data-testid="delegate-current">
            {{ c.providers[0]?.delegateEmail || '—' }}
          </span>
        </div>
      </div>

      <!-- Per-scope results. Raw scope strings on purpose: support compares
           these against the customer's Admin Console entry. -->
      <p *ngIf="scopeError" class="dwd-error">{{ scopeError }}</p>
      <div *ngIf="scopeResult as sr" class="dwd-scope-results">
        <span class="section-subtitle">
          Scopes for {{ sr.delegateEmail }} — checked
          {{ sr.checkedAt | date: 'medium' }}
        </span>
        <table class="dwd-scope-table" data-testid="dwd-scope-table">
          <thead>
            <tr>
              <th scope="col">Scope</th>
              <th scope="col">Granted</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let s of sr.scopes" [attr.data-granted]="s.granted">
              <td class="dwd-scope-name">
                {{ s.scope }}
                <span *ngIf="!s.granted && s.detail" class="dwd-scope-detail">
                  {{ s.detail }}
                </span>
              </td>
              <td>
                <span
                  class="dwd-chip"
                  [attr.data-tone]="s.granted ? 'ok' : 'error'"
                >
                  {{ s.granted ? 'Granted' : 'Missing' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Support/QA: update the delegate. Only in-app delegate write until US-5. -->
      <div class="dwd-delegate-edit">
        <input
          type="email"
          [(ngModel)]="delegateInput"
          name="delegateInput"
          placeholder="delegate@domain.edu"
          data-testid="delegate-input"
        />
        <lib-cdw-button
          level="secondary"
          type="button"
          [disabled]="updatingDelegate || !delegateInput.trim()"
          (buttonClick)="updateDelegate()"
          data-testid="delegate-update"
        >
          {{ updatingDelegate ? 'Updating…' : 'Update delegate' }}
        </lib-cdw-button>
      </div>
      <p *ngIf="delegateError" class="dwd-error">{{ delegateError }}</p>

      <p *ngIf="checkResult" class="dwd-message">{{ checkResult.message }}</p>

      <!-- Mismatch: offer the Google-reported id as a correction. -->
      <div
        *ngIf="checkResult?.status === 'id_mismatch'"
        class="dwd-mismatch"
      >
        <p>
          Update the stored Google ID to
          <strong>{{ checkResult?.actualGoogleId }}</strong>?
        </p>
        <lib-cdw-button
          level="primary"
          type="button"
          [disabled]="correcting"
          (buttonClick)="applyCorrection()"
        >
          {{ correcting ? 'Applying…' : 'Use Google’s ID' }}
        </lib-cdw-button>
        <p *ngIf="correctionError" class="dwd-error">{{ correctionError }}</p>
      </div>
    </section>

    <!-- Licensing (Chargify support diagnostic) -->
    <section class="detail-card">
      <span class="section-title">Licensing</span>
      <lib-cdw-divider thickness="1px"></lib-cdw-divider>
      <cdw-license-check-panel
        [domain]="c.primaryDomain"
      ></cdw-license-check-panel>
    </section>

    <!-- One section per enrolled provider -->
    <section class="detail-card" *ngFor="let p of c.providers">
      <span class="section-title">{{ p.providerType | uppercase }} Provider</span>
      <lib-cdw-divider thickness="1px"></lib-cdw-divider>
      <div class="detail-grid">
        <div class="detail-item">
          <span class="section-subtitle">Provider</span>
          <span class="section-content">{{ p.providerName || (p.providerType | uppercase) }}</span>
        </div>
        <div class="detail-item">
          <span class="section-subtitle">Google Customer ID</span>
          <span class="section-content">{{ p.googleId }}</span>
        </div>
        <div class="detail-item">
          <span class="section-subtitle">Provider UUID</span>
          <span class="section-content">{{ p.providerUuid }}</span>
        </div>
        <div class="detail-item">
          <span class="section-subtitle">Delegate Email</span>
          <span class="section-content">{{ p.delegateEmail || '—' }}</span>
        </div>
      </div>

      <!-- Authorized domains (EDUS-2864). Support-editable: onboarding Step 2 is
           the only other place these can be set, and it sits behind the
           `initialized` latch, so without this an onboarded tenant's domain
           list can't be corrected at all. -->
      <div class="domains">
        <span class="section-subtitle">Domains</span>
        <p class="section-subtitle">
          Users signing in from these domains resolve to this customer. The
          primary can't be removed — make another domain primary first.
        </p>
        <!-- Add sits ABOVE the list: rejections render directly beneath the
             input, and on a tenant with several domains a message below the
             list is off-screen — you'd have to scroll to find out why nothing
             happened. -->
        <div class="domain-add">
          <input
            type="text"
            [(ngModel)]="domainInput"
            name="domainInput"
            placeholder="school.edu"
            (keyup.enter)="addDomain()"
            data-testid="domain-input"
          />
          <lib-cdw-button
            level="secondary"
            type="button"
            [disabled]="addingDomain || !domainInput.trim()"
            (buttonClick)="addDomain()"
            data-testid="domain-add"
          >
            {{ addingDomain ? 'Adding…' : 'Add domain' }}
          </lib-cdw-button>
        </div>
        <!-- Shared by all three actions, so it stays next to the input where
             the eye already is, whichever one failed. -->
        <p *ngIf="domainError" class="dwd-error" data-testid="domain-error">
          {{ domainError }}
        </p>

        <div *ngIf="p.domains.length === 0" class="section-content">No domains</div>
        <ul class="domain-list">
          <li *ngFor="let d of p.domains" class="domain-row">
            <span class="domain-label">
              <span class="domain-name">{{ d.domainName }}</span>
              <span class="domain-badge primary" *ngIf="d.isPrimary">Primary</span>
            </span>
            <span class="domain-users section-subtitle">
              {{ d.userCount }} user{{ d.userCount === 1 ? '' : 's' }}
            </span>
            <!-- Always rendered, empty on the primary row, so the count column
                 lines up down the list instead of the primary's drifting out
                 to the right where the buttons would be. -->
            <span class="domain-actions">
              <ng-container *ngIf="!d.isPrimary">
                <lib-cdw-button
                  level="secondary"
                  type="button"
                  [disabled]="domainBusyId !== null"
                  (buttonClick)="confirmMakePrimary(d)"
                  data-testid="domain-make-primary"
                >
                  Make primary
                </lib-cdw-button>
                <lib-cdw-button
                  level="secondary"
                  type="button"
                  [disabled]="domainBusyId !== null"
                  (buttonClick)="confirmRemoveDomain(d)"
                  data-testid="domain-remove"
                >
                  {{ domainBusyId === d.id ? 'Working…' : 'Remove' }}
                </lib-cdw-button>
              </ng-container>
            </span>
          </li>
        </ul>
      </div>
    </section>
  </ng-container>
</div>
